import { useEffect } from 'react'
import last from 'lodash-es/last'

import {
  CARD_STAGE,
  INIT_STAGE,
  PROLONG_TIMEOUT,
  RESTORE_STAGE,
} from '../../RequestWizardForm.constants'
import {
  clearWizardUuid,
  getWizardPrevUuid,
  isRequestWizardCardLinkType,
  isRequestWizardCardVariableType,
  saveWizardUuid,
} from '../../RequestWizardForm.utils'

function useWizardLifecycle({
  // When true, restore the previous session directly into CARD_STAGE without
  // showing the RESTORE_STAGE confirmation. Set by the caller when the user
  // already agreed to restore via the localStorage draft picker.
  autoRestore = false,
  getChunk,
  isWorkflowFinished,
  prolong,
  prolongRef,
  setChunk,
  setInitLoading,
  setRestoredChunk,
  setStage,
  setUuid,
  uuid,
}) {
  async function getPrevChunk(prevUuid) {
    try {
      const { data: chunk } = await getChunk(prevUuid)
      const lastChunk = last(chunk)

      if (
        lastChunk &&
        !isRequestWizardCardVariableType(lastChunk.type) &&
        !isRequestWizardCardLinkType(lastChunk.type)
      ) {
        setStage(INIT_STAGE)
      } else if (autoRestore) {
        setChunk(chunk)
        setUuid(prevUuid)
        setStage(CARD_STAGE)
      } else {
        setRestoredChunk(chunk)
        setUuid(prevUuid)
        setStage(RESTORE_STAGE)
      }

      setInitLoading(false)
    } catch {
      // GET /chunk/ failed (typically 403 — server session expired). Fall
      // back to INIT_STAGE; the saved draft stays intact so once the user
      // re-submits INIT and lands on CARD_STAGE again, the latched draft
      // values are restored via chunkSignature match.
      setStage(INIT_STAGE)
      setInitLoading(false)
    }
  }

  useEffect(() => {
    if (uuid) {
      saveWizardUuid(uuid)
    }

    if (prolongRef.current) {
      clearInterval(prolongRef.current)
    }

    // The server expires the session together with the terminal chunk.
    if (uuid && !isWorkflowFinished) {
      prolongRef.current = setInterval(() => prolong(uuid), PROLONG_TIMEOUT)
    }
  }, [isWorkflowFinished, prolong, uuid])

  useEffect(() => {
    const prevUuid = getWizardPrevUuid()

    if (prevUuid) {
      getPrevChunk(prevUuid)
    } else {
      setStage(INIT_STAGE)
      setInitLoading(false)
    }

    return () => {
      clearWizardUuid()

      if (prolongRef.current) {
        clearInterval(prolongRef.current)
      }
    }
  }, [])
}

export default useWizardLifecycle
